import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UseScrollDirectionOptions {
    /** Threshold in pixels before direction change is detected (default: 10) */
    threshold?: number;
    /** Debounce delay to prevent excessive state changes (default: 50ms) */
    debounceDelay?: number;
}

/**
 * Custom hook to detect scroll direction
 * Returns true when scrolling down, false when scrolling up
 * 
 * @param options Configuration options
 * @returns boolean indicating scroll direction (true = down, false = up)
 */
export const useScrollDirection = (options: UseScrollDirectionOptions = {}) => {
    const {
        threshold = 10,
        debounceDelay = 50
    } = options;

    const location = useLocation(); // Track route changes
    const [scrollingDown, setScrollingDown] = useState(false);
    const lastScrollTop = useRef(0);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Reset scroll state on route change
        setScrollingDown(false);
        lastScrollTop.current = 0;

        const handleScroll = () => {
            // Try multiple scroll sources to find which one is actually scrolling
            const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const mainContentWrapper = document.querySelector('.main-content-wrapper');
            const contentFrame = document.querySelector('.content-frame');
            
            let scrollTop = windowScrollTop;
            
            // Check if main-content-wrapper is scrolling
            if (mainContentWrapper && mainContentWrapper.scrollTop > 0) {
                scrollTop = mainContentWrapper.scrollTop;
            }
            // Check if content-frame is scrolling  
            else if (contentFrame && contentFrame.scrollTop > 0) {
                scrollTop = contentFrame.scrollTop;
            }

            const scrollDifference = scrollTop - lastScrollTop.current;

            // Clear existing debounce timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Only update direction if scroll difference is above threshold
            if (Math.abs(scrollDifference) > threshold) {
                debounceTimeoutRef.current = setTimeout(() => {
                    setScrollingDown(scrollDifference > 0);
                    lastScrollTop.current = scrollTop;
                }, debounceDelay);
            }
        };

        // Add listeners to multiple potential scroll targets
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('scroll', handleScroll, { passive: true });
        
        // Also listen on the main content wrapper when it exists
        const setupElementListeners = () => {
            const mainContentWrapper = document.querySelector('.main-content-wrapper');
            const contentFrame = document.querySelector('.content-frame');
            
            if (mainContentWrapper) {
                mainContentWrapper.addEventListener('scroll', handleScroll, { passive: true });
            }
            if (contentFrame) {
                contentFrame.addEventListener('scroll', handleScroll, { passive: true });
            }
        };

        // Setup immediately and after a delay for dynamic content
        setupElementListeners();
        const timeoutId = setTimeout(setupElementListeners, 500);
        const timeoutId2 = setTimeout(setupElementListeners, 1000);

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(timeoutId2);
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            
            // Remove listeners from all potential targets
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('scroll', handleScroll);
            
            const mainContentWrapper = document.querySelector('.main-content-wrapper');
            const contentFrame = document.querySelector('.content-frame');
            if (mainContentWrapper) {
                mainContentWrapper.removeEventListener('scroll', handleScroll);
            }
            if (contentFrame) {
                contentFrame.removeEventListener('scroll', handleScroll);
            }
        };
    }, [threshold, debounceDelay, location.pathname]);

    return scrollingDown;
};
